import {
  useCreateOrderMutation,
  useEnrollAssetsMutation,
  useRedeemLicenseMutation,
} from '@gm-commercial/entitlements-client/src/api/entitlementsApi';
import { useCallback, useEffect, useRef } from 'react';

import { useActivatePeripheralsMutation } from '~/api/generated-api';
import { useAppDispatch } from '~/app/store';
import useToasts from '~/common/components/toasts/useToasts';
import type { PeripheralType } from '~/common/models/peripheral.model';
import type { ProductCodes } from '~/common/models/plans.model';
import {
  showActivationCompletionToasts,
  showReenrollmentCompletionToast,
} from './activationProgress.toasts';
import { isOrderStatusSuccessful } from './order.utils';
import {
  addActivationBatch,
  createActivationBatch,
  createReenrollmentBatch,
  markActivationCompleted,
} from './state/activationProgressSlice';

export type UseStartActivationArgs = {
  organizationId?: string;
  fleetId?: string;
};

export type StartActivationOptions = {
  /** Product label shown in the global progress card (e.g. "OnStar"). */
  productName?: string;
  /**
   * Skip the wholesale-failure toast on a submit error (missing org / rejected
   * request). Used by the activation modal, which surfaces the failure inline
   * as a payment-error banner instead of a bottom-line toast.
   */
  suppressFailureToast?: boolean;
  /**
   * When set, activation runs through the entitlements license-redeem flow
   * (prepaid) instead of the peripheral billing-activate flow (subscription).
   * Prepaid redeem is single-device — it copies the vehicle's REDEEMABLE license
   * onto the hardware — and registers a progress batch from the response's
   * `batchId`/`batchSize`, unless the redeem was idempotent (no batch).
   */
  isPrepaidRedeem?: boolean;
  /** VIN of the RPO vehicle holding the REDEEMABLE license. Required for prepaid. */
  redeemVin?: string;
  /** Hardware catalog code (e.g. DUALCAM / GTADAPTER). Required for prepaid. */
  hardwareCode?: ProductCodes;
  /** Hardware serial number (IMEI / adapter serial) to redeem. Required for prepaid. */
  hardwareId?: string;
  /**
   * Whether the redeem blocks until the Azure AI Search index reflects the
   * change before responding. Callers whose list is served by AI Search — Plan
   * Management — need the wait, or the refresh that follows reads back the
   * pre-redeem rows. The device lists query the database directly and see the
   * change immediately, so they opt out and get the response as soon as the
   * redeem is recorded.
   *
   * Defaults to `true`, the safe choice for a new caller: waiting only costs
   * latency, while skipping it on an AI-Search-backed view shows stale data.
   * Always sent explicitly, since the endpoint's own default is `false`.
   */
  waitOnAiSearchSync?: boolean;
  /**
   * When set, subscription activation runs through the billing order/enroll
   * endpoints (Shop Add-Ons) instead of the peripheral `/activate` call: a
   * first-time activation creates an order, an existing subscription enrolls the
   * assets, and the batch is registered from the response's `batchId`/`batchSize`.
   * Ignored for prepaid batches (which redeem instead).
   */
  activateViaOrder?: boolean;
  /** Product the devices enroll into (OVT/OST). Required for the order/enroll payload. */
  productCode?: ProductCodes;
  /** Whether an active subscription already exists (enroll) vs first-time (create order). */
  hasExistingSubscription?: boolean;
  /**
   * Resolved licensing-model id for a first-time order, forwarded verbatim so
   * the create-order call prices against the same model as the order preview.
   * Required for a first-time (create-order) activation; unused when an existing
   * subscription enrolls the assets.
   */
  licensingModelId?: string;
  /**
   * How many of the submitted devices the pricing preview reported as
   * re-enrollments (already enrolled and still in term). Re-enrollment is settled
   * entirely on the billing side — no vendor activation runs for those devices —
   * so they are excluded from the polled batch and their outcome is read from the
   * order status instead. Only consumed on the `activateViaOrder` path.
   */
  reenrollmentCount?: number;
};

export type UseStartActivationResult = {
  /**
   * Kicks off activation for the given peripherals. When the activate call
   * returns a `batchId` the batch is registered in the global progress slice
   * for polling; the immediate no-batch / submit-error paths emit the platform
   * completion toasts directly (unless `suppressFailureToast` is set).
   *
   * Resolves `true` when activation started (batch registered), completed
   * synchronously, or there was nothing to activate (empty selection); and
   * `false` on a submit failure so callers (e.g. the modal) can react — keep
   * the modal open and show the payment-error banner.
   */
  start: (
    peripheralIds: string[],
    peripheralType: PeripheralType,
    options?: StartActivationOptions
  ) => Promise<boolean>;
};

/**
 * Starts a peripheral activation batch (single or bulk) and hands off progress
 * tracking to the global {@link ActivationProgressSnackbar}. Completion
 * notifications go through the platform Sonner toasts so they sit with the
 * other bottom-line notifications.
 */
export const useStartActivation = ({
  organizationId,
  fleetId,
}: UseStartActivationArgs): UseStartActivationResult => {
  const dispatch = useAppDispatch();
  const [activatePeripherals] = useActivatePeripheralsMutation();
  const [redeemLicense] = useRedeemLicenseMutation();
  const [createOrder] = useCreateOrderMutation();
  const [enrollAssets] = useEnrollAssetsMutation();
  // `useToasts` returns fresh method identities each render; mirror them into a
  // ref so `start` stays referentially stable for caller `useCallback` deps.
  const { showSuccessToast, showErrorToast } = useToasts();
  const toastsRef = useRef({ showSuccessToast, showErrorToast });
  useEffect(() => {
    toastsRef.current = { showSuccessToast, showErrorToast };
  }, [showSuccessToast, showErrorToast]);

  const start = useCallback(
    async (
      peripheralIds: string[],
      peripheralType: PeripheralType,
      options?: StartActivationOptions
    ): Promise<boolean> => {
      // Empty selection: nothing to activate. Treat as a no-op success so modal
      // callers (which read a `false` return as a submit/payment failure) don't
      // surface a misleading payment-error banner when no request was made.
      if (peripheralIds.length === 0) return true;

      const total = peripheralIds.length;
      const orgId = organizationId?.trim() ?? '';
      const suppressFailureToast = options?.suppressFailureToast ?? false;

      // Activation requires a non-empty org header, so without one the request
      // is guaranteed to fail (and a returned batch couldn't be polled anyway).
      // Fail fast with the wholesale-failure toast instead of issuing a doomed
      // request.
      if (!orgId) {
        if (!suppressFailureToast) {
          showActivationCompletionToasts(
            { total, success: 0, failure: total, failed: true },
            toastsRef.current
          );
        }
        return false;
      }

      // Prepaid activation redeems the vehicle's prepaid (RPO) license onto the
      // hardware via the entitlements redeem endpoint. It is single-device, and
      // the redeem response itself carries the vendor activation batch
      // (`batchId`/`batchSize`) to hand to the progress widget.
      if (options?.isPrepaidRedeem) {
        const { redeemVin, hardwareCode, hardwareId } = options;
        // Prepaid redeem is single-device: it copies one vehicle's license onto
        // one hardware serial. A multi-id selection can't be honored (only a
        // single hardwareId is redeemed), so reject it up front and report the
        // whole selection as failed instead of silently redeeming one device and
        // over-reporting success.
        if (peripheralIds.length > 1) {
          if (!suppressFailureToast) {
            showActivationCompletionToasts(
              { total, success: 0, failure: total, failed: true },
              toastsRef.current
            );
          }
          return false;
        }
        // Redeem targets exactly one hardware serial, so completion counts always
        // reflect a single device.
        const redeemTotal = 1;
        // The redeem contract needs the source VIN plus the hardware code and
        // serial; a missing field means the request would be rejected, so fail
        // fast with the wholesale-failure toast instead of a doomed call.
        if (!redeemVin || !hardwareCode || !hardwareId) {
          if (!suppressFailureToast) {
            showActivationCompletionToasts(
              {
                total: redeemTotal,
                success: 0,
                failure: redeemTotal,
                failed: true,
              },
              toastsRef.current
            );
          }
          return false;
        }

        const redeemResult = await redeemLicense({
          orgId,
          redeemLicenseRequestDto: {
            vin: redeemVin,
            hardwareCode,
            hardwareId,
            // Whether to block on the AI-search index is the caller's to decide
            // (see `waitOnAiSearchSync`): it depends on where the list the user
            // returns to reads from. The batch comes back either way.
            waitOnAiSearchSync: options.waitOnAiSearchSync ?? true,
          },
        });

        // A redeem that worked comes back with the license it wrote, so a
        // response missing it is a failure however cheerful its envelope: the
        // no-batch branch below reads a missing batch as a device that was
        // already redeemed, and only a well-formed response supports that.
        if (
          'error' in redeemResult ||
          redeemResult.data?.success === false ||
          !redeemResult.data?.result?.license
        ) {
          if (!suppressFailureToast) {
            showActivationCompletionToasts(
              {
                total: redeemTotal,
                success: 0,
                failure: redeemTotal,
                failed: true,
              },
              toastsRef.current
            );
          }
          return false;
        }

        // A batch id means vendor activation started for the redeemed device, so
        // track it like any other activation batch instead of reporting success
        // before the device is actually on.
        const redeemedLicense = redeemResult.data?.result;
        const redeemBatchId = redeemedLicense?.batchId;
        if (redeemBatchId) {
          dispatch(
            addActivationBatch(
              createActivationBatch({
                batchId: redeemBatchId,
                orgId,
                productName: options.productName,
                peripheralType,
                fleetId,
                peripheralIds,
                // Redeem always batches the single hardware serial, so the
                // fallback matches `batchSize` in practice.
                total: redeemedLicense?.batchSize ?? redeemTotal,
              })
            )
          );
          return true;
        }

        // No batch id: the redeem was idempotent (the license already sat on
        // this hardware), so nothing was activated and there is nothing to poll.
        showActivationCompletionToasts(
          {
            total: redeemTotal,
            success: redeemTotal,
            failure: 0,
            failed: false,
          },
          toastsRef.current
        );
        dispatch(markActivationCompleted());
        return true;
      }

      // Subscription activation through the billing order/enroll endpoints
      // (Shop Add-Ons): the order/enroll response itself carries the activation
      // batch (`batchId`/`batchSize`), replacing the peripheral `/activate` call.
      // A first-time activation creates an order; an existing subscription
      // enrolls the assets under the current license.
      if (options?.activateViaOrder) {
        const { productCode, hasExistingSubscription, licensingModelId } =
          options;

        // Clamped so a stale/oversized preview count can never make the derived
        // new-enrollment count negative.
        const reenrollmentCount = Math.min(
          Math.max(options.reenrollmentCount ?? 0, 0),
          total
        );
        const newEnrollmentCount = total - reenrollmentCount;

        // Fast-fail on an incomplete payload: both endpoints need the product
        // code, and a first-time create-order also needs the preview-resolved
        // licensing-model id. Reject up front (mirroring the prepaid branch)
        // rather than issue a request that would serialize `undefined`.
        if (!productCode || (!hasExistingSubscription && !licensingModelId)) {
          if (!suppressFailureToast) {
            showActivationCompletionToasts(
              { total, success: 0, failure: total, failed: true },
              toastsRef.current
            );
          }
          return false;
        }

        const enrolledEntities = { PERIPHERALS: peripheralIds };
        const orderResult = hasExistingSubscription
          ? await enrollAssets({
              requestEnrollAssetsDto: {
                organizationId: orgId,
                productCode,
                enrolledEntities,
                // Plan-management surfaces wait for the AI-search index to sync
                // (`true`) so the subscription table refreshes immediately after
                // enroll. Device-list surfaces opt out (`false`) and rely on the
                // returned `batchId`/`batchSize` for the progress widget to poll.
                waitOnAiSearchSync: options?.waitOnAiSearchSync ?? false,
              },
            })
          : await createOrder({
              createOrderRequestDto: {
                organizationId: orgId,
                productCode,
                // Guard above guarantees the id on the first-time order path.
                licensingModelId: licensingModelId as string,
                enrolledEntities,
                waitOnAiSearchSync: options?.waitOnAiSearchSync ?? false,
              },
            });

        const orderData = 'error' in orderResult ? undefined : orderResult.data;
        const order = orderData?.result;
        const orderBatchId = order?.batchId;
        // Billing accepted the order. This is the *only* outcome signal for
        // re-enrolled devices, which never get a vendor batch to poll.
        const isBillingSuccessful =
          Boolean(orderData) &&
          orderData?.success !== false &&
          isOrderStatusSuccessful(order?.status);

        // A batch id means the vendor activation started for the newly-enrolled
        // devices, so it must be polled — even when billing reported a failure
        // status, because the batch is the authoritative source for those
        // devices. Re-enrollments in the same order still take their outcome from
        // the order status, reported as their own line on the summary.
        if (orderBatchId) {
          dispatch(
            addActivationBatch(
              createActivationBatch({
                batchId: orderBatchId,
                orgId,
                productName: options.productName,
                peripheralType,
                fleetId,
                // A mixed activation only knows *how many* devices were
                // re-enrolled, not which ones, so submitting the full selection
                // as the retry target could re-activate a device that is already
                // in term. Omit it and let the retry work off the failed ids the
                // batch status reports.
                ...(reenrollmentCount === 0 && { peripheralIds }),
                // `batchSize` is the authoritative count for the vendor batch;
                // fall back to the preview-derived new-enrollment count so the
                // progress bar always has a denominator.
                total: order?.batchSize ?? newEnrollmentCount,
                ...(reenrollmentCount > 0 && {
                  reenrollmentCount,
                  reenrollmentFailed: !isBillingSuccessful,
                }),
              })
            )
          );
          return true;
        }

        // No batch id. That is expected when every device was a re-enrollment
        // (billing-only, nothing for the vendor to activate), in which case a
        // successful order is a completed activation.
        if (isBillingSuccessful && reenrollmentCount === total) {
          dispatch(markActivationCompleted());
          if (reenrollmentCount > 1) {
            // Report through the completion card, like any other bulk outcome.
            dispatch(
              addActivationBatch(
                createReenrollmentBatch({
                  orgId,
                  productName: options.productName,
                  peripheralType,
                  fleetId,
                  reenrollmentCount,
                })
              )
            );
          } else {
            showReenrollmentCompletionToast(
              { count: reenrollmentCount },
              toastsRef.current
            );
          }
          return true;
        }

        // Everything else — a rejected request, a `success: false` body, a failure
        // status, or a missing batch id when devices still needed activating —
        // means the activation never started. Surface the failure (the modal shows
        // its inline error banner) rather than a progress bar or success toast.
        if (!suppressFailureToast) {
          showActivationCompletionToasts(
            { total, success: 0, failure: total, failed: true },
            toastsRef.current
          );
        }
        return false;
      }

      const result = await activatePeripherals({
        'x-bd-organization': orgId,
        ...(fleetId && { 'x-bd-fleet': fleetId }),
        activatePeripheralsRequestDto: {
          peripheralIds,
          peripheralType: String(peripheralType),
        },
      });

      // Submit failure (the batch never started): surface an error toast,
      // mirroring the polled wholesale-failure path so failures are never
      // silent. No batch is registered, so there is no completion card.
      if ('error' in result || result.data?.success === false) {
        if (!suppressFailureToast) {
          showActivationCompletionToasts(
            { total, success: 0, failure: total, failed: true },
            toastsRef.current
          );
        }
        return false;
      }

      const batchId = result.data?.result?.batchId;
      if (batchId) {
        dispatch(
          addActivationBatch(
            createActivationBatch({
              batchId,
              orgId,
              productName: options?.productName,
              peripheralType,
              fleetId,
              peripheralIds,
              total,
            })
          )
        );
        return true;
      }

      // Synchronous activation (no batch id): treat as fully succeeded.
      showActivationCompletionToasts(
        { total, success: total, failure: 0, failed: false },
        toastsRef.current
      );
      dispatch(markActivationCompleted());
      return true;
    },
    [
      activatePeripherals,
      redeemLicense,
      createOrder,
      enrollAssets,
      organizationId,
      fleetId,
      dispatch,
    ]
  );

  return { start };
};
